import React from "react";
import { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box, Paper, Typography, TextField, Button, CircularProgress, Tooltip } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import CreateIcon from "@mui/icons-material/Create";
import {
  ApiGet,
  ApiPost,
  ApiPut,
  ApiDelete,
} from "../helpers/API/ApiData";

import DeleteIcon from "@mui/icons-material/Delete";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";

import { DataTable, TableColumnDef } from "../components/DataTable";
import { SelfHostedEditor } from "../components/TextEditor";
import { PageHeader } from "../components/PageHeader";

import { createSanitizedMarkup } from "../utils/createSanitizedMarkup";
import { usePermissions } from "../hooks/usePermissions";
import { useContentLanguage } from "../hooks";
import { LanguageTabs } from "../components/LanguageTabs";

interface EditorState {
  content: string;
  content_ger: string;
}

export default function LegalNotice() {
  const { canCreate, canEdit, canDelete } = usePermissions("/legal-notice");
  const [lang, setLang] = useContentLanguage();
  const [noticeData, setNoticeData] = useState<any>({});
  const [addNotice, setAddNotice] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllNotice, setGetAllNotice] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [noticeIdForDelete, setNoticeIdForDelete] = useState<any>();
  const [noticeIdForEdit, setNoticeIdForEdit] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<number>(0);

  const [editorState, setEditorState] = useState<EditorState>({
    content: "",
    content_ger: "",
  });

  useEffect(() => {
    getAllLegalNotice();
  }, [page, countPerPage, search]);

  const addImpreintModelClose = (_e: any) => {
    setAddNotice(false);
    setIsEditApi(false);
    setErrors({});
    setNoticeData({});
    setEditorState({ content: "", content_ger: "" });
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setNoticeData({ ...noticeData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const formValidation = () => {
    let formIsValid = true;
    const errors: any = {};
    if (!noticeData?.legalNoticeName_ger?.trim()) {
      formIsValid = false;
      errors["legalNoticeName_ger"] = "*Bitte geben Sie den Namen ein (DE)";
    }
    if (!noticeData?.legalNoticeName?.trim()) {
      formIsValid = false;
      errors["legalNoticeName"] = "*Bitte geben Sie den Namen ein (EN)";
    }
    if (!editorState?.content_ger) {
      formIsValid = false;
      errors["legalNoticeDescription_ger"] = "*Bitte geben Sie eine Beschreibung ein (DE)";
    }
    if (!editorState?.content) {
      formIsValid = false;
      errors["legalNoticeDescription"] = "*Bitte geben Sie eine Beschreibung ein (EN)";
    }
    setErrors(errors);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const getAllLegalNotice = async () => {
    setLoaderForGetAll(true);
    const searchParam = search ? `&letter=${search}` : "";
    await ApiGet(`legal-notice/find?page=${page}&limit=${countPerPage}${searchParam}`)
      .then((res: any) => {
        setGetAllNotice(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const addLegalNotice = async (_e: any) => {
    setIsEditApi(false);
    if (formValidation()) {
      setLoader(true);
      const data = {
        legalNoticeName: noticeData?.legalNoticeName,
        legalNoticeName_ger: noticeData?.legalNoticeName_ger,
        legalNoticeDescription: editorState?.content,
        legalNoticeDescription_ger: editorState?.content_ger,
      };

      await ApiPost(`legal-notice/create`, data)
        .then(() => {
          toast.success("Rechtsnachweis erfolgreich hinzuzufügen");
          setAddNotice(false);
          setIsEditApi(false);
          getAllLegalNotice();
          setNoticeData({});
          setEditorState({ content: "", content_ger: "" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const editLegalNotice = async (_e: any) => {
    if (formValidation()) {
      setLoader(true);
      const data = {
        legalNoticeName: noticeData?.legalNoticeName,
        legalNoticeName_ger: noticeData?.legalNoticeName_ger,
        legalNoticeDescription: editorState?.content,
        legalNoticeDescription_ger: editorState?.content_ger,
      };

      await ApiPut(`legal-notice/update?id=${noticeIdForEdit}`, data)
        .then(() => {
          toast.success("rechtliche Mitteilung erfolgreich");
          getAllLegalNotice();
          setAddNotice(false);
          setIsEditApi(false);
          setNoticeData({});
          setEditorState({ content: "", content_ger: "" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const deleteLegalNotice = async () => {
    await ApiDelete(`legal-notice/delete?id=${noticeIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Rechtsnachweis erfolgreich löschen");
          setShow(false);
          setPage(1);
          getAllLegalNotice();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  // Content is sanitized via the createSanitizedMarkup utility (uses DOMPurify)
  const columns: TableColumnDef[] = [
    {
      header: "Titel",
      render: (row: any) => (lang === "de" ? row?.legalNoticeName_ger : row?.legalNoticeName) || "-",
      getValue: (row: any) => lang === "de" ? row?.legalNoticeName_ger : row?.legalNoticeName,
    },
    {
      header: "Datum",
      render: (row: any) => moment(row?.createdAt).format("DD.MM.YYYY"),
      getValue: (row: any) => row?.createdAt,
    },
    {
      header: "Einstellungen",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {canEdit && (
            <Tooltip title="Hinweis bearbeiten" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setIsEditApi(true);
                  setAddNotice(true);
                  setNoticeIdForEdit(row?._id);
                  setEditorState({
                    content: row?.legalNoticeDescription || "",
                    content_ger: row?.legalNoticeDescription_ger || "",
                  });
                  setNoticeData({
                    legalNoticeName: row?.legalNoticeName,
                    legalNoticeName_ger: row?.legalNoticeName_ger,
                  });
                }}
              >
                <CreateIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Hinweis löschen" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setNoticeIdForDelete(row?._id);
                  setShow(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="Rechtliche Hinweise"
            onSearch={(val) => { setSearch(val); setPage(1); }}
            onAdd={canCreate ? () => setAddNotice(true) : undefined}
          />
        </Box>
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllNotice || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie dieses Rechtliche Hinweise löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Nein</Button>
          <Button onClick={() => deleteLegalNotice()} color="error" variant="contained">Ja</Button>
        </DialogActions>
      </Dialog>

      {addNotice && (
        <Dialog fullScreen open={addNotice}>
          <Toolbar>
            <IconButton edge="end" color="inherit" onClick={addImpreintModelClose} aria-label="close" sx={{ ml: "auto" }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Column headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Deutsch</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>English</Typography>
                </Box>
              </Box>

              {/* Name row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Name</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small" fullWidth variant="outlined"
                    name="legalNoticeName_ger"
                    value={noticeData?.legalNoticeName_ger || ""}
                    onChange={onhandleChange}
                  />
                  <Typography variant="caption" color="error">{errors["legalNoticeName_ger"]}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small" fullWidth variant="outlined"
                    name="legalNoticeName"
                    value={noticeData?.legalNoticeName || ""}
                    onChange={onhandleChange}
                  />
                  <Typography variant="caption" color="error">{errors["legalNoticeName"]}</Typography>
                </Box>
              </Box>

              {/* Description row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Beschreibung</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content_ger: editor?.getContent() }));
                    }}
                    initialValue={editorState.content_ger}
                  />
                  <Typography variant="caption" color="error">{errors["legalNoticeDescription_ger"]}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content: editor?.getContent() }));
                    }}
                    initialValue={editorState.content}
                  />
                  <Typography variant="caption" color="error">{errors["legalNoticeDescription"]}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                disabled={loader}
                onClick={(e) => { isEditApi ? editLegalNotice(e) : addLegalNotice(e); }}
              >
                {isEditApi ? "Speichern" : "Hinzufügen"}
                {loader && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
}
